import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  BarChart3,
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
  ClipboardList,
  LayoutGrid,
  LogOut,
  Menu,
  Package,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
  Store,
  TicketPercent,
  Users,
  X,
  Zap,
} from "lucide-react";

import logoIcon from "../../assets/logo.png";
import wholesumWordmark from "../../assets/wholesum.svg";
import restaurantFoodActions from "../../assets/restaurant-food-actions.png";
import { env } from "../../config/env";
import {
  clearAuthFlow,
  clearAuthSession,
  getAuthUser,
} from "../AdminAuth/components/authStore";
import FoodNotificationsBell from "./components/FoodNotificationsBell";
import FoodManualAssignmentPopup from "./components/FoodManualAssignmentPopup";

const linkBase =
  "group relative flex h-11 w-full shrink-0 items-center gap-3 rounded-[11px] px-3 text-[14px] font-bold transition-all duration-200 before:absolute before:bottom-1.5 before:left-0 before:top-1.5 before:w-[3px] before:rounded-full before:opacity-0 after:ml-auto after:text-[18px] after:font-normal after:leading-none after:content-['›'] [&>svg]:h-[18px] [&>svg]:w-[18px] [&>svg]:shrink-0 [&>span]:min-w-0 [&>span]:flex-1 [&>span]:truncate [&>span]:text-left";

const navItems = [
  {
    to: "/admin/food-dashboard",
    label: "Dashboard",
    icon: LayoutGrid,
    end: true,
  },
  {
    to: "/admin/food-dashboard/restaurants",
    label: "Restaurants",
    icon: Store,
  },
  {
    to: "/admin/food-dashboard/employees",
    label: "Employees",
    icon: Users,
  },
  {
    to: "/admin/food-dashboard/items",
    label: "Food Catalog",
    icon: Package,
  },
  {
    to: "/admin/food-dashboard/approvals",
    label: "Approval Requests",
    icon: ClipboardCheck,
  },
  {
    to: "/admin/food-dashboard/orders",
    label: "Orders",
    icon: ClipboardList,
  },
  {
    to: "/admin/food-dashboard/coupons",
    label: "Coupons",
    icon: TicketPercent,
  },
  {
    to: "/admin/food-dashboard/reports",
    label: "Reports",
    icon: BarChart3,
  },
  {
    to: "/admin/food-dashboard/settings",
    label: "Settings",
    icon: Settings,
  },
];

function getDisplayName(user) {
  return (
    user?.name ||
    user?.fullName ||
    user?.adminName ||
    user?.username ||
    user?.email?.split("@")?.[0] ||
    "Admin"
  );
}

function getDisplayRole(user) {
  return String(user?.role || "Admin")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function getProfileImage(user) {
  return (
    user?.profileImage ||
    user?.profile_image ||
    user?.avatar ||
    user?.avatarUrl ||
    user?.image ||
    user?.onboarding?.documents?.profilePhoto?.url ||
    ""
  );
}

function toAbsoluteFileUrl(path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const base = env?.AUTH_BASE_URL || "";
  return `${base}${path}`;
}

function titleFromPath(pathname) {
  if (pathname.includes("/restaurants")) return "RESTAURANTS";
  if (pathname.includes("/employees")) return "EMPLOYEES";
  if (pathname.includes("/items")) return "FOOD CATALOG";
  if (pathname.includes("/approvals")) return "APPROVAL REQUESTS";
  if (pathname.includes("/orders")) return "ORDERS";
  if (pathname.includes("/coupons")) return "COUPONS";
  if (pathname.includes("/reports")) return "REPORTS";
  if (pathname.includes("/settings")) return "SETTINGS";
  if (pathname.includes("/notifications")) return "NOTIFICATIONS";
  if (pathname.includes("/profile")) return "PROFILE";
  return "DASHBOARD";
}

function ProfileMenu() {
  const user = getAuthUser() || {};
  const navigate = useNavigate();

  const displayName = getDisplayName(user);
  const displayRole = getDisplayRole(user);
  const profileImage = toAbsoluteFileUrl(getProfileImage(user));
  const initial = displayName?.charAt(0)?.toUpperCase() || "A";

  return (
    <div className="relative group">
      <button
        type="button"
        onClick={() => navigate("/admin/dashboard/profile")}
        className="flex h-12 items-center gap-2.5 rounded-[17px] border border-white/90 bg-white/80 px-2.5 shadow-[0_8px_22px_rgba(37,48,78,0.07)] ring-1 ring-slate-200/70 backdrop-blur-md transition-all hover:-translate-y-0.5 hover:border-[#f2c6cc] hover:bg-white sm:h-[54px] sm:px-3"
      >
        <div className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full border border-[#f2c6cc] bg-[linear-gradient(135deg,#ffe8ed,#ffd3dc)] sm:h-10 sm:w-10">
          {profileImage ? (
            <img
              src={profileImage}
              alt="Profile"
              className="h-full w-full object-cover"
            />
          ) : (
            <span className="text-[15px] font-bold text-[#d86a78]">
              {initial}
            </span>
          )}
        </div>

        <div className="hidden text-left md:block">
          <div className="text-[10px] font-medium text-slate-400">Logged in as</div>
          <div className="max-w-[112px] truncate text-[12px] font-black text-slate-800">
            {displayName}
          </div>
        </div>

        <ChevronDown
          size={14}
          className="hidden text-slate-700 transition-transform duration-300 group-hover:rotate-180 md:block"
        />
      </button>

      <div className="pointer-events-none absolute right-0 top-[calc(100%+10px)] z-50 w-[280px] translate-y-2 rounded-[18px] border border-[#f2c6cc] bg-white p-5 opacity-0 shadow-[0_20px_50px_rgba(0,0,0,0.12)] transition-all duration-200 group-hover:pointer-events-auto group-hover:translate-y-0 group-hover:opacity-100">
        <div className="flex items-center gap-4">
          <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full border-2 border-[#f2c6cc] bg-[#d86a78]/10">
            {profileImage ? (
              <img
                src={profileImage}
                alt="Profile"
                className="h-full w-full object-cover"
              />
            ) : (
              <span className="text-[22px] font-bold text-[#d86a78]">
                {initial}
              </span>
            )}
          </div>

          <div className="min-w-0">
            <div className="truncate text-[16px] font-bold text-[#d86a78]">
              {displayName}
            </div>
            <div className="mt-1 text-[13px] text-slate-500">
              {displayRole}
            </div>
          </div>
        </div>

        <div className="my-4 h-px bg-[#f2c6cc]" />

        <div className="space-y-3 text-sm">
          <div>
            <div className="text-[11px] uppercase text-slate-400">Email</div>
            <div className="truncate text-slate-700">{user?.email || "-"}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase text-slate-400">Role</div>
            <div className="text-slate-700">{displayRole}</div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => navigate("/admin/dashboard/profile")}
          className="mt-5 w-full rounded-[12px] border border-[#f2c6cc] bg-[#fff8f9] px-4 py-2 text-sm font-semibold text-[#d86a78] transition hover:bg-white"
        >
          My Profile
        </button>
      </div>
    </div>
  );
}

function SidebarContent({
  onNavigate,
  onLogout,
  collapsed = false,
  onToggleCollapse = null,
  showCollapseControl = false,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [actionsOpen, setActionsOpen] = useState(false);

  useEffect(() => {
    setActionsOpen(false);
  }, [location.pathname]);

  const linkClass = ({ isActive }) =>
    [
      linkBase,
      collapsed ? "justify-center px-0 after:hidden [&>span]:hidden" : "",
      isActive
        ? "bg-gradient-to-r from-[#ffe9ee] to-[#fff3f5] text-[#e34f68] shadow-[0_7px_18px_rgba(224,91,116,0.09)] before:bg-[#ff3f67] before:opacity-100 after:text-[#ed5870]"
        : "text-[#526783] after:text-[#8fa0b8] hover:bg-[#fff8f9] hover:text-[#d86a78] hover:after:translate-x-0.5 hover:after:text-[#d86a78]",
    ]
      .filter(Boolean)
      .join(" ");

  const go = (to) => {
    setActionsOpen(false);
    onNavigate?.();
    navigate(to);
  };

  const handleLogoutClick = () => {
    setActionsOpen(false);
    onNavigate?.();
    onLogout?.();
  };

  return (
    <div className="flex h-full min-h-0 flex-col overflow-visible bg-[linear-gradient(180deg,#ffffff_0%,#fbfcff_72%,#fff8fa_100%)]">
      <div className={`shrink-0 ${collapsed ? "px-3 pt-3" : "px-4 pt-3"}`}>
        <div
          className={`flex h-[58px] items-center ${
            collapsed ? "justify-center" : "justify-between gap-3"
          }`}
        >
          <div
            className={`flex min-w-0 items-center ${
              collapsed ? "justify-center" : "gap-2.5"
            }`}
          >
            <img
              src={logoIcon}
              alt="Nefazo"
              className={`${
                collapsed ? "h-10 w-10" : "h-11 w-11"
              } shrink-0 select-none object-contain`}
              draggable={false}
            />

            {!collapsed ? (
              <img
                src={wholesumWordmark}
                alt="Nefazo"
                className="h-[30px] min-w-0 max-w-[118px] select-none object-contain"
                draggable={false}
              />
            ) : null}
          </div>

          {showCollapseControl && !collapsed ? (
            <button
              type="button"
              onClick={onToggleCollapse}
              className="grid h-9 w-9 shrink-0 place-items-center rounded-[12px] border border-slate-200/80 bg-white text-slate-500 shadow-[0_6px_16px_rgba(15,23,42,0.06)] transition hover:border-rose-200 hover:bg-rose-50 hover:text-[#d86a78]"
              aria-label="Collapse restaurant sidebar"
              title="Collapse sidebar"
            >
              <PanelLeftClose size={17} />
            </button>
          ) : null}
        </div>

        <div className="mt-2 h-px w-full bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
      </div>

      {showCollapseControl && collapsed ? (
        <button
          type="button"
          onClick={onToggleCollapse}
          className="mx-auto mt-3 grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-[#f2c6cc] bg-[#fff6f7] text-[#d86a78] shadow-sm transition hover:bg-white"
          aria-label="Expand restaurant sidebar"
          title="Expand sidebar"
        >
          <PanelLeftOpen size={17} />
        </button>
      ) : null}

      <div
        className={`${
          collapsed ? "mt-2 px-2" : "mt-2 pl-3 pr-1.5"
        } min-h-0 flex-1 overflow-hidden`}
      >
        <nav className="sidebar-scroll flex h-full min-h-0 flex-col gap-0.5 overflow-y-auto overscroll-contain pr-1.5">
          {navItems.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              onClick={onNavigate}
              className={linkClass}
              title={collapsed ? label : undefined}
            >
              <Icon size={18} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
      </div>

      <div
        className={`z-20 shrink-0 border-t border-rose-100/70 bg-white/95 shadow-[0_-10px_24px_rgba(15,23,42,0.035)] backdrop-blur-xl ${
          collapsed ? "px-2 pb-3 pt-2" : "px-3 pb-3 pt-2"
        }`}
      >
        <div className="relative">
          <div
            className={`absolute bottom-[calc(100%+8px)] left-0 right-0 transition-all duration-300 ease-out ${
              actionsOpen
                ? "pointer-events-auto translate-y-0 opacity-100"
                : "pointer-events-none translate-y-4 opacity-0"
            } ${collapsed ? "left-0 right-auto" : ""}`}
          >
            <div
              className={`${
                collapsed ? "w-[210px]" : "w-full"
              } space-y-2 rounded-[18px] border border-rose-100 bg-white/95 p-2.5 shadow-[0_22px_55px_rgba(15,23,42,0.16)] backdrop-blur-xl`}
            >
              <button
                type="button"
                onClick={() => go("/admin/dashboard")}
                className="flex w-full items-center justify-center gap-3 rounded-[12px] bg-[#d86a78] px-5 py-3 text-[15px] font-semibold text-white transition hover:bg-[#c95b69]"
              >
                <ArrowLeft size={18} />
                <span>Main Admin</span>
              </button>

              <button
                type="button"
                onClick={handleLogoutClick}
                className="flex w-full items-center justify-center gap-3 rounded-[12px] border border-[#f2c6cc] bg-white px-5 py-3 text-[15px] font-semibold text-slate-400 transition hover:bg-slate-50 hover:text-[#d86a78]"
              >
                <LogOut size={18} />
                <span>Logout</span>
              </button>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setActionsOpen((current) => !current)}
            className={`group/actions relative flex w-full overflow-hidden rounded-[14px] border text-[12px] font-semibold transition-all focus:outline-none focus:ring-4 focus:ring-rose-100 ${
              collapsed
                ? "h-11 items-center justify-center px-0"
                : "min-h-[118px] items-start px-3 py-3 text-left"
            } ${
              actionsOpen
                ? "border-[#e96578] bg-[linear-gradient(135deg,#fff0f3,#ffe4ea)] text-[#d94f66] shadow-[0_10px_24px_rgba(216,106,120,0.15)]"
                : "border-[#f2c6cc] bg-[linear-gradient(145deg,#fff5f7_0%,#ffe8ed_100%)] text-[#d86a78] hover:-translate-y-0.5 hover:shadow-[0_10px_24px_rgba(216,106,120,0.13)]"
            }`}
            aria-label={actionsOpen ? "Close sidebar actions" : "Open sidebar actions"}
            aria-expanded={actionsOpen}
          >
            {collapsed ? (
              <Zap size={18} />
            ) : (
              <>
                <span className="relative z-10 grid h-8 w-8 shrink-0 place-items-center rounded-[10px] bg-white/80 text-[#ff4e70] shadow-sm">
                  <Zap size={17} fill="currentColor" />
                </span>

                <span className="relative z-10 ml-2 min-w-0 flex-1">
                  <span className="block text-[15px] font-black text-[#df4e68]">
                    Actions
                  </span>
                  <span className="mt-0.5 block text-[8px] font-medium text-slate-400">
                    Quick tools for restaurant operations
                  </span>
                </span>

                <ChevronUp
                  size={15}
                  className={`relative z-10 mt-1 shrink-0 transition-transform duration-300 ${
                    actionsOpen ? "rotate-180" : ""
                  }`}
                />

                <img
                  src={restaurantFoodActions}
                  alt=""
                  aria-hidden="true"
                  className="pointer-events-none absolute left-4 top-[52px] w-[82px] max-w-none opacity-95 drop-shadow-[0_8px_12px_rgba(84,48,46,0.10)] transition-transform duration-500 group-hover/actions:scale-105"
                />

                <span className="absolute bottom-3 right-3 rotate-[-8deg] text-right text-[9px] font-semibold italic leading-tight text-[#e75970]">
                  Save Time
                  <br />
                  Do More
                </span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function Sidebar({ onLogout, collapsed, onToggleCollapse }) {
  return (
    <aside
      className={`fixed left-0 top-0 z-40 hidden h-screen shrink-0 border-r border-slate-200 bg-white shadow-[4px_0_24px_rgba(15,23,42,0.025)] transition-[width] duration-300 lg:block ${
        collapsed ? "w-[78px]" : "w-[232px]"
      }`}
    >
      <SidebarContent
        onLogout={onLogout}
        collapsed={collapsed}
        onToggleCollapse={onToggleCollapse}
        showCollapseControl
      />
    </aside>
  );
}

function MobileSidebar({ open, onClose, onLogout }) {
  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      <button
        type="button"
        aria-label="Close menu"
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />

      <aside className="absolute left-0 top-0 flex h-full w-[280px] max-w-[86vw] flex-col bg-white shadow-xl">
        <div className="flex items-center justify-between px-4 pt-4">
          <div className="text-[14px] font-semibold text-slate-500">Restaurant Menu</div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
            aria-label="Close menu"
          >
            <X size={18} />
          </button>
        </div>

        <div className="mt-2 h-px w-full bg-slate-200/70" />

        <div className="min-h-0 flex-1">
          <SidebarContent
            onNavigate={onClose}
            onLogout={onLogout}
            collapsed={false}
          />
        </div>
      </aside>
    </div>
  );
}

export default function FoodAdminLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("foodSidebarCollapsed") === "1";
  });
  const location = useLocation();
  const navigate = useNavigate();
  const pageTitle = titleFromPath(location.pathname);

  const toggleSidebar = () => {
    setSidebarCollapsed((current) => {
      const next = !current;
      try {
        window.localStorage.setItem("foodSidebarCollapsed", next ? "1" : "0");
      } catch {}
      return next;
    });
  };

  const handleLogout = () => {
    clearAuthFlow?.();
    clearAuthSession?.();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#f7f9fc]">
      <Sidebar
        onLogout={handleLogout}
        collapsed={sidebarCollapsed}
        onToggleCollapse={toggleSidebar}
      />

      <MobileSidebar
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        onLogout={handleLogout}
      />

      <main
        className={`min-h-screen bg-[radial-gradient(circle_at_78%_0%,rgba(244,114,182,0.065),transparent_24%),linear-gradient(180deg,#f9fbfe_0%,#f6f8fc_100%)] transition-[padding] duration-300 ${
          sidebarCollapsed ? "lg:pl-[78px]" : "lg:pl-[232px]"
        }`}
      >
        <header
          className={`fixed left-0 right-0 top-0 z-30 bg-[#f7f9fc]/90 p-2 backdrop-blur-xl transition-[left] duration-300 sm:p-2.5 ${
            sidebarCollapsed ? "lg:left-[78px]" : "lg:left-[232px]"
          }`}
        >
          <div className="relative flex h-[68px] items-center gap-3 overflow-visible rounded-[22px] border border-white/90 bg-[linear-gradient(105deg,rgba(255,255,255,0.97)_0%,rgba(255,249,250,0.96)_58%,rgba(255,247,241,0.94)_100%)] px-3 shadow-[0_10px_30px_rgba(37,48,78,0.07)] ring-1 ring-slate-200/70 sm:h-[76px] sm:px-4 lg:px-5">
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 overflow-hidden rounded-[22px]"
            >
              <div className="absolute -right-16 -top-24 h-56 w-56 rounded-full bg-rose-200/20 blur-3xl" />
              <div className="absolute left-[235px] top-[-95px] hidden h-[190px] w-[230px] xl:block xl:left-[285px] 2xl:left-[350px]">
                <img
                  src={restaurantFoodActions}
                  alt=""
                  className="h-full w-full object-contain opacity-75 drop-shadow-[0_10px_18px_rgba(80,48,45,0.08)]"
                />
              </div>
            </div>

            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="relative z-10 grid h-11 w-11 shrink-0 place-items-center rounded-[15px] border border-rose-100 bg-white/90 text-[#ee5870] shadow-[0_7px_18px_rgba(216,106,120,0.10)] transition-all hover:-translate-y-0.5 hover:bg-[#fff6f7] sm:h-[52px] sm:w-[52px] lg:hidden"
              aria-label="Open menu"
            >
              <Menu size={20} />
            </button>

            <span className="relative z-10 hidden h-10 w-px bg-slate-200/80 sm:block lg:hidden" />

            <div className="relative z-10 min-w-0">
              <div className="truncate text-[14px] font-black tracking-[-0.01em] text-[#101828] sm:text-[17px]">
                <span className="text-[#d86a78]">{pageTitle}</span>
              </div>
              <div className="hidden text-[11px] font-semibold text-slate-400 sm:block">
                Restaurant Operations
              </div>
            </div>

            <div className="relative z-10 ml-auto flex items-center gap-2 sm:gap-3">
              <button
                type="button"
                onClick={() => navigate("/admin/dashboard")}
                className="group/main hidden h-11 items-center gap-2 rounded-[15px] border border-rose-200/80 bg-gradient-to-r from-rose-50/90 to-white/90 px-3 text-[11px] font-black text-[#e34661] shadow-[0_8px_20px_rgba(225,91,116,0.10)] backdrop-blur-md transition-all hover:-translate-y-0.5 hover:bg-white hover:shadow-[0_12px_26px_rgba(225,91,116,0.16)] md:inline-flex sm:h-[54px] sm:gap-3 sm:px-4"
                title="Open Main Admin Dashboard"
              >
                <span className="grid h-8 w-8 place-items-center rounded-[11px] bg-[#ffe9ed] text-[#e94864] sm:h-9 sm:w-9">
                  <ArrowLeft size={16} />
                </span>
                <span className="hidden xl:inline">Main Admin</span>
                <ArrowRight
                  size={16}
                  className="hidden transition-transform duration-300 group-hover/main:translate-x-1 xl:block"
                />
              </button>

              <span className="hidden h-10 w-px bg-slate-200/80 md:block" />
              <FoodNotificationsBell />
              <span className="hidden h-10 w-px bg-slate-200/80 md:block" />
              <ProfileMenu />
            </div>
          </div>
        </header>

        <div className="mx-auto w-full max-w-[1920px] px-3 pb-4 pt-[100px] sm:px-5 sm:pb-5 sm:pt-[116px] lg:px-7 lg:pb-6 lg:pt-[120px]">
          <FoodManualAssignmentPopup />
          <Outlet />
        </div>
      </main>
    </div>
  );
}
